import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import { AuthServiceInjectionToken } from '@fantaz/shared/data-access/api';
import { LoggerService } from '@fantaz/shared/utils/log';

import * as AuthActions from './auth.actions';
import { AuthIdentity } from './auth.models';
import { AuthState } from './auth.reducer';
import {
  getAuthLoaded,
  getAuthUser,
  getCurrentUser,
  getLoggedIn,
  getMembershipInfo,
  getUserMemberShipAndTaxCompleted,
  isVerified,
  playerProfileComplete,
  selectAuthUserContactPoint,
  selectAuthUserDevice,
  selectAuthUserIdentities,
  selectAuthUserSessionPayload,
} from './auth.selectors';

@Injectable()
export class AuthFacade {
  #authService = inject(AuthServiceInjectionToken);
  store = inject(Store);

  /**
   * Combine pieces of state using createSelector,
   * and expose them as observables through the facade.
   */
  loaded$ = this.store.select(getAuthLoaded);
  authUser$ = this.store.select(getAuthUser);
  currentUser$ = this.store.select(getCurrentUser);
  membership$ = this.store.select(getMembershipInfo);
  isLoggedIn$ = this.store.select(getLoggedIn);
  memberShipAndTaxCompleted$ = this.store.select(
    getUserMemberShipAndTaxCompleted,
  );
  playerProfileComplete$ = this.store.select(playerProfileComplete);

  isVerified$ = this.store.select(isVerified);

  /*
   * Get data over current user
   */
  isZbo$: Observable<boolean> = this.membership$.pipe(
    map((membership) => membership?.type === 'ZBO'),
  );

  /*
   * Get identities over authenticated user
   */
  userIdentities$: Observable<AuthIdentity[]> = this.store.select(
    selectAuthUserIdentities,
  );
  /*
   * Get session over authenticated user
   */
  userSessionPayload$ = this.store.select(selectAuthUserSessionPayload);

  authUserDevice$ = this.store.select(selectAuthUserDevice);

  contactPoint = toSignal(this.store.select(selectAuthUserContactPoint));

  currentUser = toSignal(this.currentUser$);

  isLoggedIn = toSignal(this.isLoggedIn$);

  constructor(private readonly logger: LoggerService) {}

  /**
   * Use the initialization action to perform one
   * or more tasks in your Effects.
   */
  init() {
    this.logger.log('AuthFacade.init()');
    this.store.dispatch(AuthActions.loadUser());
  }

  refreshCurrentUser() {
    this.store.dispatch(
      AuthActions.AuthCurrentUserActions.loadCurrentUserInProgress(),
    );
  }

  relogin(data: { username: string; password: string }) {
    this.store.dispatch(AuthActions.relogin(data));
  }

  logout() {
    this.store.dispatch(AuthActions.AuthAuthActions.logout());
  }

  clearSession() {
    this.#authService.clearAuthState();
  }
}
